<template>
  <v-dialog
    :value="true"
    persistent
    scrollable
    max-width="720"
    content-class="ext-edit-dialog"
  >
    <v-card class="ext-edit-card">
      <!-- スクロールしても内容と合計・印刷導線を見失わないようヘッダーは固定 -->
      <div class="ext-edit-header">
        <div class="ext-edit-header__main">
          <div class="ext-edit-title">
            {{ courseName }}
          </div>

          <div class="ext-edit-total">
            <v-progress-circular
              v-if="isCalculating"
              indeterminate
              size="18"
              width="2"
            />
            <template v-else-if="estimatedTotal !== null">
              <span class="ext-edit-total__amount">¥{{ Number(estimatedTotal).toLocaleString() }}</span>
              <span class="ext-edit-total__label">注文後のお会計</span>
            </template>
          </div>

          <v-btn
            icon
            :ripple="false"
            aria-label="閉じる"
            class="ext-edit-close"
            @click="$emit('close')"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </div>

        <div class="ext-edit-header__actions">
          <print-btn
            subject="概算伝票"
            :print="printEstimatedSlip"
          >
            <template #append>
              <preview :slip-svg="slipSvg" />
            </template>
          </print-btn>
        </div>
      </div>

      <div class="ext-edit-body">
        <div
          v-for="line in lines"
          :key="line.key"
          class="ext-edit-row"
        >
          <div class="ext-edit-row__head">
            <div class="ext-edit-row__label">
              {{ line.label }}
            </div>

            <v-select
              v-if="line.vendibleOptions"
              :value="line.temp.value.vendibleId"
              :items="line.vendibleOptions"
              item-text="name"
              item-value="id"
              dense
              hide-details
              class="ext-edit-vendible-select"
              :aria-label="`${line.label}の商品`"
              @change="changeVendible(line.temp, $event, line.vendibleOptions)"
            />

            <v-btn
              v-if="line.removable"
              icon
              small
              :ripple="false"
              :aria-label="`${line.label}を削除`"
              class="ext-edit-row__remove"
              @click="removeLine(line)"
            >
              <v-icon small>
                {{ icons.mdiTrashCanOutline }}
              </v-icon>
            </v-btn>
          </div>

          <div class="ext-edit-row__controls">
            <v-text-field
              :value="line.temp.value.sellingPrice"
              type="number"
              dense
              hide-details
              prefix="¥"
              class="ext-edit-price-field"
              :aria-label="`${line.label}の単価`"
              @input="changeSellingPrice(line.temp, $event)"
            />

            <div class="ext-edit-stepper">
              <v-btn
                icon
                small
                :ripple="false"
                :disabled="line.temp.size <= 1"
                :aria-label="`${line.label}の数量を減らす`"
                @click="changeSize(line.temp, line.temp.size - 1)"
              >
                <v-icon small>
                  {{ icons.mdiMinus }}
                </v-icon>
              </v-btn>

              <span class="ext-edit-stepper__value">{{ line.temp.size }}</span>

              <v-btn
                icon
                small
                :ripple="false"
                :aria-label="`${line.label}の数量を増やす`"
                @click="changeSize(line.temp, line.temp.size + 1)"
              >
                <v-icon small>
                  {{ icons.mdiPlus }}
                </v-icon>
              </v-btn>
            </div>

            <v-btn-toggle
              :value="line.temp.value.taxCharge"
              mandatory
              dense
              class="ext-edit-tax-toggle"
              :aria-label="`${line.label}の料金区分`"
              @change="changeTaxCharge(line.temp, $event)"
            >
              <v-btn
                v-for="taxOption in taxOptions"
                :key="taxOption.value"
                :value="taxOption.value"
                small
                :ripple="false"
                :aria-label="taxOption.label"
              >
                {{ taxOption.label }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <v-select
            v-if="line.kind === 'nomination'"
            :value="recipientUserIds(line.temp)"
            :items="castOptions"
            item-text="name"
            item-value="id"
            multiple
            dense
            hide-details
            placeholder="受領キャスト"
            class="ext-edit-recipient-select mt-2"
            :aria-label="`${line.label}の受領キャスト`"
            @change="changeRecipients(line.temp, $event)"
          />

          <div class="ext-edit-row__subtotal">
            小計 ¥{{ lineSubtotal(line.temp).toLocaleString() }}
          </div>
        </div>

        <div class="ext-edit-add">
          <v-btn
            text
            small
            :ripple="false"
            :disabled="nominationOptions.length === 0"
            :aria-label="`${nominationAlias}を追加`"
            @click="addNomination"
          >
            <v-icon
              small
              left
            >
              {{ icons.mdiPlus }}
            </v-icon>
            {{ nominationAlias }}を追加
          </v-btn>
        </div>
      </div>

      <div class="ext-edit-footer">
        <v-btn
          :ripple="false"
          class="ext-edit-footer__btn"
          :loading="isHolding"
          :disabled="isBusy"
          aria-label="次の延長に設定"
          @click="hold"
        >
          次の延長に設定
        </v-btn>

        <v-btn
          color="primary"
          :ripple="false"
          class="ext-edit-footer__btn"
          :loading="isExtending"
          :disabled="isBusy || disabled"
          aria-label="即延長"
          @click="extendNow"
        >
          即延長
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  inject,
  watch,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  cloneDeep,
  compact,
  filter,
  find,
  first,
  map,
} from 'lodash'
import {
  mdiClose,
  mdiMinus,
  mdiPlus,
  mdiTrashCanOutline,
} from '@mdi/js'
import VendingHistoryApi from '@/api/waiter/VendingHistory'
import ExtensionPlanApi from '@/api/waiter/ExtensionPlan'
import useCurrentData from '@/views/composable/useCurrentData'
import useVendingHistory from '@/views/composable/useVendingHistory'
import useExtensionEstimation, { getTotalTax } from '@/views/composable/useExtensionEstimation'
import useExtensionSlipPrint from '@/views/composable/useExtensionSlipPrint'
import { buildExtensionPlanPayload } from '@/views/composable/useExtensionPlan'
import PrintBtn from '@/views/components/util/PrintBtn.vue'
import Preview from '@/views/components/printer/Preview.vue'

const toVendibleOptions = vendibles => {
  return map(vendibles, vendible => ({
    id: Number(vendible.id),
    name: vendible.attributes?.name,
    sellingPrice: vendible.attributes?.sellingPrice,
  }))
}

export default {
  components: {
    PrintBtn,
    Preview,
  },
  props: {
    // NOTE: { course, nominations, items } のvendingHistoryTemp束。候補カード・保持プランのどちらからも同じ形で渡す
    seed: {
      type: Object,
      required: true,
    },
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    customName: {
      type: String,
      default: '',
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      default: null,
    },
    customerCount: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    total: {
      type: [Number, String],
      required: true,
    },
    paidInAdvanceAmount: {
      type: [Number, String],
      default: 0,
    },
    totalDepositAmount: {
      type: [Number, String],
      default: 0,
    },
    // 自動延長の発火窓など、注文自体を受け付けない状態（設定の保存は可能）
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const courses = inject('courses', ref([]))
    const nominations = inject('nominations', ref([]))
    const items = inject('items', ref([]))
    const users = inject('users', ref([]))

    const { currentClub } = useCurrentData()
    const {
      buildVendingHistoryTemp,
      buildVendingHistoriesToSubmit,
      setVendingContributionsAttributes,
    } = useVendingHistory()

    const courseTemp = reactive(cloneDeep(props.seed.course))
    const nominationTemps = ref(map(props.seed.nominations || [], temp => reactive(cloneDeep(temp))))
    const itemTemps = ref(map(props.seed.items || [], temp => reactive(cloneDeep(temp))))

    const isHolding = ref(false)
    const isExtending = ref(false)
    const isBusy = computed(() => isHolding.value || isExtending.value)

    const vendingHistoriesTemp = computed(() => {
      return compact([courseTemp, ...nominationTemps.value, ...itemTemps.value])
    })

    const { estimatedTotal, isCalculating } = useExtensionEstimation({
      tableId: () => props.tableId,
      vendingHistoriesTemp,
    })

    const courseName = computed(() => {
      const targetCourse = find(courses.value, course => Number(course.id) === Number(courseTemp.value.vendibleId))

      return targetCourse?.attributes?.name || '延長セット'
    })

    const courseOptions = computed(() => toVendibleOptions(courses.value))
    const nominationOptions = computed(() => toVendibleOptions(nominations.value))
    const itemOptions = computed(() => toVendibleOptions(items.value))

    const nominationAlias = computed(() => currentClub.value?.nominationAlias || '指名')

    const castOptions = computed(() => {
      return map(
        filter(users.value, user => user.attributes?.role === 'cast'),
        user => ({ id: Number(user.id), name: user.attributes?.name }),
      )
    })

    const lines = computed(() => {
      return [
        {
          key: 'course',
          kind: 'course',
          temp: courseTemp,
          label: `${currentClub.value?.courseAlias || 'セット'}料金`,
          removable: false,
          vendibleOptions: courseOptions.value,
        },
        ...map(nominationTemps.value, (temp, index) => ({
          key: `nomination-${index}`,
          kind: 'nomination',
          temp,
          label: nominationAlias.value,
          removable: true,
          vendibleOptions: nominationOptions.value,
        })),
        ...map(itemTemps.value, (temp, index) => ({
          key: `item-${index}`,
          kind: 'item',
          temp,
          label: 'アイテム',
          removable: true,
          vendibleOptions: itemOptions.value,
        })),
      ]
    })

    const lineSubtotal = temp => (Number(temp.value.sellingPrice) || 0) * temp.size

    const changeSellingPrice = (temp, value) => {
      // eslint-disable-next-line no-param-reassign
      temp.value.sellingPrice = value === '' ? 0 : Number(value)
    }

    const changeSize = (temp, size) => {
      if (size < 1) return

      // eslint-disable-next-line no-param-reassign
      temp.size = size
    }

    const changeTaxCharge = (temp, taxCharge) => {
      // eslint-disable-next-line no-param-reassign
      temp.value.taxCharge = taxCharge
    }

    // NOTE: 差し替え時は差し替え先の定価に戻す（前の商品の金額が残ると事故になる）
    const changeVendible = (temp, vendibleId, vendibleOptions) => {
      const target = find(vendibleOptions, option => Number(option.id) === Number(vendibleId))
      if (!target) return

      // eslint-disable-next-line no-param-reassign
      temp.value.vendibleId = target.id
      // eslint-disable-next-line no-param-reassign
      temp.value.sellingPrice = Number(target.sellingPrice) || 0
    }

    // NOTE: 延長にその場で指名を足せるようにする（旧UIの延長候補カードと同じ編集力）。
    // 初期値は先頭の指名の定価・既定税区分。差し替えは既存の商品セレクトで行う
    const addNomination = () => {
      const targetNomination = first(nominations.value)
      if (!targetNomination) return

      nominationTemps.value = [
        ...nominationTemps.value,
        reactive(buildVendingHistoryTemp({
          vendibleId: Number(targetNomination.id),
          vendibleType: 'Nomination',
          sellingPrice: Number(targetNomination.attributes?.sellingPrice) || 0,
          taxCharge: targetNomination.attributes?.defaultTaxCharge || 'normal',
        })),
      ]
    }

    const removeLine = line => {
      const targetTemps = line.kind === 'nomination' ? nominationTemps : itemTemps
      targetTemps.value = filter(targetTemps.value, temp => temp !== line.temp)
    }

    const recipientUserIds = temp => {
      return map(temp.value.vendibleRecipientsAttributes || [], recipient => Number(recipient.userId))
    }

    // NOTE: 受領キャストが複数のときの分け前はUI上は等分に固定する（個別比率は注文画面で調整する運用）
    const changeRecipients = (temp, userIds) => {
      const ratio = userIds.length > 0 ? 1.0 / userIds.length : 0

      // eslint-disable-next-line no-param-reassign
      temp.value.vendibleRecipientsAttributes = map(userIds, userId => ({ userId: Number(userId), ratio }))
    }

    // NOTE: 受領キャストの変更に追従して売上計上先を振り直す（候補カードと同じ挙動）
    watch(() => cloneDeep(vendingHistoriesTemp.value), () => {
      setVendingContributionsAttributes({ targetVendingHistoriesTemp: vendingHistoriesTemp.value })
    })

    // 概算伝票は「編集後の金額」で出す（割引後の金額を提示する運用があるため）
    const extensionCandidates = computed(() => [{
      courseHistoryTemp: courseTemp,
      nominationHistoriesTemp: nominationTemps.value,
      itemHistoriesTemp: itemTemps.value,
      newHistoriesTemp: [],
    }])

    const estimatedTotals = computed(() => {
      if (estimatedTotal.value === null) return []

      return [{ extensionCandidateIdx: 0, amount: estimatedTotal.value }]
    })

    const { slipSvg, printEstimatedSlip } = useExtensionSlipPrint({
      props,
      extensionCandidates,
      estimatedTotals,
      getTotalTax,
    })

    const buildPlan = () => buildExtensionPlanPayload({
      courseTemp,
      nominationTemps: nominationTemps.value,
      itemTemps: itemTemps.value,
    })

    const hold = async () => {
      if (isBusy.value) return

      isHolding.value = true

      try {
        const res = await ExtensionPlanApi.updateExtensionPlan({
          tableId: props.tableId,
          extensionPlan: buildPlan(),
        })

        if (res?.status === 200) {
          vm.$toast.success('次の延長に設定しました')
          emit('held', { extensionPlan: buildPlan(), table: res.data.table.data })
          emit('close')
        } else {
          vm.$toast.error('設定に失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('設定に失敗しました')
      } finally {
        isHolding.value = false
      }
    }

    const extendNow = async () => {
      if (isBusy.value) return
      if (props.disabled) return

      isExtending.value = true

      try {
        const res = await VendingHistoryApi.createVendingHistories({
          tableId: props.tableId,
          vendingHistories: buildVendingHistoriesToSubmit(vendingHistoriesTemp.value),
        })

        if (res?.status === 200) {
          // NOTE: プランは消費しない（卓が終わるまで有効）。2回目以降の延長・自動延長にも同じ内容が使われる
          emit('extended', res.data)
          emit('close')
        } else {
          vm.$toast.error('延長に失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('延長に失敗しました')
      } finally {
        isExtending.value = false
      }
    }

    return {
      courseName,
      lines,
      castOptions,
      nominationOptions,
      nominationAlias,
      estimatedTotal,
      isCalculating,
      isHolding,
      isExtending,
      isBusy,
      slipSvg,

      lineSubtotal,
      recipientUserIds,
      changeSellingPrice,
      changeSize,
      changeTaxCharge,
      changeVendible,
      changeRecipients,
      addNomination,
      removeLine,
      printEstimatedSlip,
      hold,
      extendNow,

      taxOptions: [
        { value: 'normal', label: '通常' },
        { value: 'included', label: '込み' },
        { value: 'cut', label: 'カット' },
      ],
      icons: {
        mdiClose,
        mdiMinus,
        mdiPlus,
        mdiTrashCanOutline,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.ext-edit-card {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  font-size: 14px;
}

.ext-edit-header {
  flex: 0 0 auto;
  padding: 12px 16px 8px;
  border-bottom: 1px solid var(--overview-border, rgba(28, 28, 46, 0.1));
}

.ext-edit-header__main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ext-edit-title {
  flex: 1;
  min-width: 0;
  font-size: 18px;
  font-weight: 700;
}

.ext-edit-total {
  text-align: right;
  line-height: 1.2;
}

.ext-edit-total__amount {
  display: block;
  font-size: 19px;
  font-weight: 800;
}

.ext-edit-total__label {
  display: block;
  font-size: 11px;
  opacity: 0.6;
}

.ext-edit-close {
  flex: 0 0 auto;
}

.ext-edit-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.ext-edit-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 16px 12px;
}

.ext-edit-row {
  padding: 12px 0;
  border-bottom: 1px solid var(--overview-border, rgba(28, 28, 46, 0.1));
}

.ext-edit-row__head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ext-edit-row__label {
  font-weight: 700;
  white-space: nowrap;
}

.ext-edit-vendible-select {
  flex: 1;
  min-width: 0;
}

.ext-edit-row__remove {
  flex: 0 0 auto;
}

.ext-edit-row__controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}

.ext-edit-price-field {
  max-width: 140px;
}

.ext-edit-stepper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ext-edit-stepper__value {
  min-width: 24px;
  text-align: center;
  font-weight: 700;
}

// タブレット運用での視認性優先（候補カードと揃える）
.ext-edit-tax-toggle .v-btn {
  font-size: 13px;
}

.ext-edit-row__subtotal {
  margin-top: 8px;
  text-align: right;
  font-weight: 700;
}

.ext-edit-add {
  display: flex;
  justify-content: flex-start;
  padding-top: 8px;
}

.ext-edit-footer {
  flex: 0 0 auto;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid var(--overview-border, rgba(28, 28, 46, 0.1));
}

.ext-edit-footer__btn {
  flex: 1;
  height: 44px;
}
</style>
